import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const initial = {
  full_name: "",
  email: "",
  company: "",
  role: "",
  consent: false,
  website: "",
};

function labels(mode) {
  if (mode === "tickets_live") {
    return {
      title: "Get your ticket.",
      lead: "Ticketing goes live shortly. Leave your details and we\u2019ll send you the checkout link the moment it opens \u2014 with priority for Early Access members.",
      cta: "Get tickets",
    };
  }
  if (mode === "waitlist") {
    return {
      title: "Join the waitlist.",
      lead: "The room is nearly full. Add your details and we\u2019ll be in touch as soon as a seat opens.",
      cta: "Join the waitlist",
    };
  }
  return {
    title: "Be first in line.",
    lead: "Tickets are not on sale yet. Early Access members hear everything first \u2014 before it reaches this page.",
    cta: "Join Early Access",
  };
}

const PERKS = [
  { n: "01", t: "Speaker announcements", d: "Every confirmed name lands in your inbox first." },
  { n: "02", t: "Priority ticket window", d: "48 hours ahead of public sale, at the best price." },
  { n: "03", t: "The run-up briefing", d: "A short, sharp update as the program takes shape." },
];

function Field({ id, label, ...props }) {
  return (
    <div className="field-u">
      <label htmlFor={id}>{label}</label>
      <input id={id} {...props} />
    </div>
  );
}

export default function EarlyAccess({ settings }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const L = labels(settings?.mode || "early_access");

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Please tick the consent box to continue."); return; }
    if (!form.full_name || !form.email || !form.company || !form.role) { setError("Please fill in every field."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/early-access", form);
      setDone(true);
      toast.success(res.data?.message || "You\u2019re on the list.");
      setForm(initial);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409) {
        setDone(true);
        toast.success(formatApiError(detail) || "You\u2019re already on the list \u2014 see you in Bergen.");
      } else if (status === 429) {
        setError(formatApiError(detail) || "Too many requests. Please try again shortly.");
      } else {
        setError(formatApiError(detail) || "Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="early-access"
      data-testid="early-access-section"
      className="section border-t border-[var(--rule)] relative"
    >
      <div className="ea-topline absolute inset-x-0 top-[-1px] h-[2px]" aria-hidden />

      <div className="max-w-page container-x grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        {/* Pitch */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="section-index">07</span>
            <span className="eyebrow" data-testid="ea-eyebrow">Early access</span>
          </div>
          <h2
            className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.98] tracking-[-0.02em] max-w-[14ch]"
            data-testid="ea-title"
          >
            {L.title}
          </h2>
          <p
            className="text-[15px] md:text-[16px] text-[var(--fg-muted)] leading-[1.55] mt-7 max-w-[42ch]"
            data-testid="ea-lead"
          >
            {L.lead}
          </p>

          <div className="mt-10 border-t border-[var(--rule)]">
            {PERKS.map((p) => (
              <div
                key={p.n}
                className="grid grid-cols-12 gap-4 py-5 border-b border-[var(--rule-soft)]"
                data-testid={`ea-perk-${p.n}`}
              >
                <span className="col-span-2 text-[11px] font-semibold tracking-[0.2em] text-[var(--fg-muted)] tabular-nums pt-1">
                  {p.n}
                </span>
                <div className="col-span-10">
                  <p className="text-[15px] font-medium tracking-[-0.01em]">{p.t}</p>
                  <p className="text-[13px] text-[var(--fg-muted)] mt-1 leading-[1.5]">{p.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="md:col-span-6 md:col-start-7">
          {done ? (
            <div
              data-testid="early-access-success"
              className="ea-success border border-[var(--fg)] p-9 md:p-12 flex flex-col gap-5"
            >
              <span className="ea-success-mark" aria-hidden>
                <Check size={22} strokeWidth={2.5} />
              </span>
              <p className="font-display text-3xl md:text-[40px] leading-[1.05] tracking-[-0.01em] max-w-[20ch]">
                You&rsquo;re on the list. See you in Bergen.
              </p>
              <p className="text-[14px] text-[var(--fg-muted)] max-w-[46ch] leading-[1.55]">
                We&rsquo;ll send the first speaker announcement to your inbox before
                anyone else sees it.
              </p>
              <button
                type="button"
                className="text-link self-start mt-1"
                onClick={() => setDone(false)}
                data-testid="early-access-add-another"
              >
                Add another <span aria-hidden>→</span>
              </button>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-9 md:pt-4"
              data-testid="early-access-form"
              noValidate
            >
              <div style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }} aria-hidden>
                <label>
                  Website
                  <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={onChange} />
                </label>
              </div>

              <Field id="ea-name" label="Full name" name="full_name" value={form.full_name} onChange={onChange} placeholder="First Last" autoComplete="name" required data-testid="ea-input-name" />
              <Field id="ea-email" label="Work email" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@company.com" autoComplete="email" required data-testid="ea-input-email" />
              <Field id="ea-company" label="Company" name="company" value={form.company} onChange={onChange} placeholder="Company or organisation" autoComplete="organization" required data-testid="ea-input-company" />
              <Field id="ea-role" label="Role" name="role" value={form.role} onChange={onChange} placeholder="e.g. Head of AI" autoComplete="organization-title" required data-testid="ea-input-role" />

              <label
                htmlFor="ea-consent"
                className="sm:col-span-2 flex items-start gap-3.5 text-[13px] text-[var(--fg-muted)] leading-[1.5] cursor-pointer select-none pt-1"
                data-testid="ea-consent-wrap"
              >
                <input
                  id="ea-consent"
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                  className="ea-check"
                  data-testid="ea-input-consent"
                />
                <span>
                  I consent to Norwegian AI Summit storing my information and
                  contacting me about the event. Unsubscribe any time —
                  no sharing, no reselling.
                </span>
              </label>

              {error && (
                <p className="sm:col-span-2 text-[13px] text-red-600" data-testid="ea-error">
                  {error}
                </p>
              )}

              <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5 pt-2 border-t border-[var(--rule)] mt-1">
                <p className="text-[11px] text-[var(--fg-muted)] uppercase tracking-[0.2em] pt-4 sm:pt-0">
                  Notification list · Phase 01
                </p>
                <button
                  type="submit"
                  className="btn-accent btn-accent--lg justify-center sm:justify-start"
                  disabled={submitting}
                  data-testid="ea-submit"
                >
                  {submitting ? "Sending…" : L.cta}
                  <ArrowRight size={16} strokeWidth={2} className="arrow" />
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
